import React from 'react';
import type { GroupBase, MultiValueProps } from 'react-select';
import * as styles from './EntitySelectMultiValue.module.scss';
import Avatar from '../../components/Avatar';
import Pill from '../../components/Pill';
import type { OptionTypeBase } from '../../components/Select';
import PostPill from '../../post/PostPill';
import TopicPill from '../../topic/TopicPill';

type EntitySelectMultiValueProps<
  OptionType extends OptionTypeBase = { label: string; value: string },
  IsMulti extends boolean = boolean,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
> = MultiValueProps<OptionType, IsMulti, GroupType> & {
  selectProps: MultiValueProps<
    OptionType,
    IsMulti,
    GroupType
  >['selectProps'] & {
    PostPillComponent?: React.ElementType;
    postPillComponentProps?: Record<string, unknown>;
  };
};

const EntitySelectMultiValue = <
  OptionType extends OptionTypeBase = { label: string; value: string },
  IsMulti extends boolean = boolean,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>({
  children,
  data,
  innerProps,
  removeProps: { onClick: onRemove },
  selectProps: { PostPillComponent = PostPill, postPillComponentProps = {} },
}: EntitySelectMultiValueProps<OptionType, IsMulti, GroupType>) => {
  const { type, icon, tooltip } = data;
  let inner = null;

  if (type === 'topic') {
    inner = (
      <TopicPill
        topic={data.topic}
        icon={icon}
        tooltip={tooltip}
        onRemove={onRemove}
        size="small"
      />
    );
  } else if (type === 'post') {
    inner = (
      <PostPillComponent
        {...postPillComponentProps}
        post={data.post}
        icon={icon}
        tooltip={tooltip}
        onRemove={onRemove}
        size="small"
      />
    );
  } else {
    inner = (
      <Pill
        type={type}
        onClick={onRemove}
        icon={type === 'user' && <Avatar size="small" user={data.user} />}
        size="small"
      >
        {children}
      </Pill>
    );
  }

  return (
    <div className={styles.container} {...innerProps}>
      {inner}
    </div>
  );
};

export default EntitySelectMultiValue;
